import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { parkingsService } from '../services/parkings';

interface Parking {
  id: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  type: string;
  hourlyRate: number | null;
  dailyRate: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ParkingsState {
  parkings: Parking[];
  isLoading: boolean;
  error: string | null;
  total: number;
  currentPage: number;
  pageSize: number;
}

const initialState: ParkingsState = {
  parkings: [],
  isLoading: false,
  error: null,
  total: 0,
  currentPage: 1,
  pageSize: 10,
};

export const fetchParkings = createAsyncThunk(
  'parkings/fetch',
  async ({ page = 1, pageSize = 10 }: { page?: number; pageSize?: number } = {}) => {
    const response = await parkingsService.getParkings({ page, pageSize });
    return (response as any).data;
  }
);

export const createParking = createAsyncThunk(
  'parkings/create',
  async (data: any) => {
    const response = await parkingsService.createParking(data);
    return (response as any).data;
  }
);

export const updateParking = createAsyncThunk(
  'parkings/update',
  async ({ id, data }: { id: string; data: any }) => {
    const response = await parkingsService.updateParking(id, data);
    return (response as any).data;
  }
);

export const deleteParking = createAsyncThunk(
  'parkings/delete',
  async (id: string) => {
    await parkingsService.deleteParking(id);
    return id;
  }
);

const parkingsSlice = createSlice({
  name: 'parkings',
  initialState,
  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchParkings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchParkings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.parkings = action.payload.parkings || [];
        state.total = action.payload.pagination?.total || 0;
      })
      .addCase(fetchParkings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Erreur lors du chargement des parkings';
      })
      .addCase(createParking.fulfilled, (state, action) => {
        state.parkings.unshift(action.payload);
        state.total += 1;
      })
      .addCase(updateParking.fulfilled, (state, action) => {
        const index = state.parkings.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.parkings[index] = action.payload;
        }
      })
      .addCase(deleteParking.fulfilled, (state, action) => {
        state.parkings = state.parkings.filter(p => p.id !== action.payload);
        state.total -= 1;
      });
  },
});

export const { setCurrentPage, setPageSize } = parkingsSlice.actions;

export default parkingsSlice.reducer;